import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';
import Header from '@/components/Header/Header';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { Loading } from '@/components/Loading';
import { IconList } from '@/icons';
import Button from '@/components/core/Button';
import { DepartmentDetailsForm } from '@/components/Departments/DepartmentDetailsForm';
import { DepartmentTravellers } from '@/components/Departments/DepartmentTravellers';
import { DepartmentAdvanced } from '@/components/Departments/DepartmentAdvanced';
import {
  isValidView,
  getViewsForType,
  getDefaultView,
} from '@/utils/viewHelpers';
import { DataNotFound } from '@/components/DataNotFound';
import { ViewPaths } from '@/types/views';
import {
  useGetDepartmentByID,
  DepartmentDTO,
  useGetCurrentDepartmentTree,
} from '@/api/trafikportalen';
import { DepartmentPayerForm } from '@/components/Departments/DepartmentPayerForm';
import { OrganizationCreditInfo } from '@/components/OrganizationCreditInfo';
import { DepartmentCreditInfo } from '@/components/DepartmentCreditInfo';
import { DepartmentAdmins } from '@/components/Departments/DepartmentAdmins';
import {
  canAccessDepartmentResource,
  hasDepartmentAccess,
} from '@/helpers/departmentPermissions';
import { FormattedMessage } from 'react-intl';
import { DocumentHead } from '@/components/core/DocumentHead';

export interface LocalDepartment extends Partial<DepartmentDTO> {
  children?: LocalDepartment[];
  id: string;
}

export const Route = createFileRoute(
  '/$tenantId/_layout/departments/_layout/$departmentId/$view',
)({
  component: DepartmentView,
  beforeLoad: ({ params: { tenantId, departmentId, view } }) => {
    if (!isValidView(view, 'department')) {
      throw redirect({
        to: '/$tenantId/departments/$departmentId/$view',
        params: {
          tenantId,
          departmentId,
          view: getDefaultView('department'),
        },
      });
    }
  },
});

function DepartmentView() {
  const { tenantId, departmentId, view } = Route.useParams();
  const selected = view as ViewPaths<'department'>;
  const navigate = useNavigate();

  const { formatMessage } = useIntl();

  const { data: departmentTree, isLoading: tLoading } =
    useGetCurrentDepartmentTree();

  const { data: department, isLoading: dLoading } = useGetDepartmentByID(
    departmentId,
    {
      query: {
        enabled: hasDepartmentAccess(departmentId),
      },
    },
  );

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, departmentId, view },
        replace: false,
      });
    },
    [navigate, tenantId, departmentId],
  );

  const views = useMemo(() => {
    let views = getViewsForType('department');

    if (!department?.parent_id) {
      views = views.filter((view) => view.path !== 'admins');
    }

    return views;
  }, [department?.parent_id]);

  const departmentHasChildDepartments = useMemo(() => {
    if (!departmentTree || !department) return false;

    const findDepartment = (dep: LocalDepartment) => {
      if (!dep) return;

      if (dep.id === departmentId) {
        return dep;
      }
      for (const child of dep.children ?? []) {
        const departmentFound = findDepartment(child) as LocalDepartment;
        if (departmentFound) {
          return departmentFound;
        }
      }
      return undefined;
    };
    const departmentFound = findDepartment(departmentTree);
    return (departmentFound?.children?.length ?? 0) > 0;
  }, [department, departmentId, departmentTree]);

  const metadataKeys = useMemo(() => {
    const departmentName = department?.name || 'Avdelning';

    switch (selected) {
      case 'information':
        return {
          title: `${departmentName} - Information`,
          description: `Information och inställningar för avdelningen ${departmentName}`,
        };
      case 'travellers':
        return {
          title: `${departmentName} - Resenärer`,
          description: `Resenärer i avdelningen ${departmentName}`,
        };
      case 'admins':
        return {
          title: `${departmentName} - Administratörer`,
          description: `Administratörer för avdelningen ${departmentName}`,
        };
      case 'advanced':
        return {
          title: `${departmentName} - Avancerat`,
          description: `Avancerade inställningar för avdelningen ${departmentName}`,
        };
      default:
        return {
          title: `${departmentName}`,
          description: `Detaljer för avdelningen ${departmentName}`,
        };
    }
  }, [selected, department?.name]);

  if (tLoading || dLoading) {
    return <Loading />;
  }

  if (!canAccessDepartmentResource(departmentId)) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Du har inte tillgång till denna avdelning" />
        }
      />
    );
  }

  if (!department) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Avdelningen finns inte" />
        }
      />
    );
  }

  if (!departmentTree) {
    return (
      <DataNotFound
        description={<FormattedMessage defaultMessage="Något gick fel" />}
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={department.name}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
        actions={
          <Button
            className="w-full text-text-700 md:hidden md:w-fit"
            startIcon={<IconList />}
            onClick={() =>
              navigate({
                to: '/$tenantId/departments',
                params: { tenantId },
                search: { departmentId: departmentId },
                replace: false,
              })
            }
            variant="outline"
          >
            {formatMessage({ defaultMessage: 'Avdelningar' })}
          </Button>
        }
      />
      <div className="flex w-full flex-col px-4 pb-20 md:px-8 lg:pb-0">
        {selected === 'information' && (
          <>
            {department.parent_id ? (
              <DepartmentCreditInfo
                departmentId={department.id}
                className="border-b border-b-background-200 py-4 md:py-8"
              />
            ) : (
              <OrganizationCreditInfo className="border-b border-b-background-200 py-4 md:py-8" />
            )}
            <DepartmentDetailsForm
              key={department.id}
              department={department}
            />
            <DepartmentPayerForm departmentId={department.id!} />
          </>
        )}
        {selected === 'travellers' && (
          <DepartmentTravellers departmentId={department.id!} />
        )}

        {selected === 'admins' && department.parent_id && (
          <DepartmentAdmins departmentId={department.id!} />
        )}

        {selected === 'advanced' && (
          <DepartmentAdvanced
            departmentId={department.id!}
            parentDepartmentId={department.parent_id!}
            hasChildDepartments={departmentHasChildDepartments}
          />
        )}
      </div>
    </div>
  );
}
